import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import { Delete, GripVertical, Hide, Unhide, SortAZ } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import SingleCheckbox from "@/components/ui/SingleCheckbox";
import SingleSelect from "@/components/controls/SingleSelect";
import AddActionButton from "@/components/ui/add-action-button";
import { NONE_OPTION_VALUE } from "@/components/controls/Form/Form";
import { IconButton } from "@/components/ui/icon-button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "@/components/ui/tooltip";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { OptionItem } from "../utils/fieldFormUtils";

const OPTIONS_MANAGER_TRANSLATION_KEY =
  "settings.deals.fieldManagement.optionsManager";

interface OptionsManagerData {
  options: string[];
  optionItems?: OptionItem[];
  allowUsersToAdd: boolean;
  defaultValue: string;
}

interface OptionsManagerProps {
  initialOptions?: Array<string>;
  initialOptionItems?: OptionItem[];
  initialAllowUsersToAdd?: boolean;
  initialDefaultValue?: string;
  fieldType: "single" | "multi";
  onOptionsChange: (data: OptionsManagerData) => void;
  onHiddenToggle?: (id: number | string) => void;
  // Gate run before a hide takes effect — resolves false to cancel.
  onBeforeHide?: (item: OptionItem) => Promise<boolean>;
  isPresetFieldEdit?: boolean;
  isEditableSystemField?: boolean;
}

const OptionsManager: React.FC<OptionsManagerProps> = ({
  initialOptions = [""],
  initialOptionItems,
  initialAllowUsersToAdd = false,
  initialDefaultValue = "",
  onOptionsChange,
  onHiddenToggle,
  onBeforeHide,
  isPresetFieldEdit = false,
  isEditableSystemField = false
}) => {
  const { t } = useTranslation();
  const { enabled: showSystemFieldDefaultEntry } = useFeatureFlag(
    FeatureFlags.CRM_5207_SYSTEM_FIELD_DEFAULT_ENTRY
  );
  const { enabled: configurableSystemFieldsEnabled, loading: configurableSystemFieldsLoading } =
    useFeatureFlag(FeatureFlags.CRM_5705_CONFIGURABLE_SYSTEM_FIELDS);
  const [options, setOptions] = useState<string[]>(
    Array.isArray(initialOptions) && initialOptions.length > 0
      ? initialOptions
      : [""]
  );
  // Kept index-aligned with options so id/isHidden/isSystem never drift from
  // their row; mirror names when the caller supplies no items.
  const [optionItems, setOptionItems] = useState<OptionItem[]>(
    initialOptionItems ??
      (Array.isArray(initialOptions) && initialOptions.length > 0
        ? initialOptions
        : [""]
      ).map((name) => ({ name }))
  );
  const [allowUsersToAdd, setAllowUsersToAdd] = useState<boolean>(
    initialAllowUsersToAdd
  );
  const [defaultValue, setDefaultValue] = useState<string>(initialDefaultValue);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const newInputRef = useRef<HTMLInputElement>(null);
  const [focusNewInput, setFocusNewInput] = useState(false);

  const safeOptions =
    Array.isArray(options) && options.length > 0 ? options : [""];

  const notifyParent = (
    newOptions: string[],
    newAllowUsersToAdd: boolean,
    newDefaultValue: string,
    newOptionItems: OptionItem[]
  ) => {
    onOptionsChange({
      options: newOptions,
      // Send aligned items so the form keeps real id/flags per row (not name-derived).
      optionItems: newOptionItems,
      allowUsersToAdd: newAllowUsersToAdd,
      defaultValue: newDefaultValue
    });
  };

  useEffect(() => {
    if (focusNewInput && newInputRef.current) {
      newInputRef.current.focus();
      setFocusNewInput(false);
    }
  }, [focusNewInput, safeOptions.length]);

  // Update all controlled state atomically when parent props change.
  useEffect(() => {
    const newOptions =
      Array.isArray(initialOptions) && initialOptions.length > 0
        ? initialOptions
        : [""];

    setOptions(newOptions);
    setAllowUsersToAdd(initialAllowUsersToAdd);
    setDefaultValue(initialDefaultValue);
    setOptionItems(initialOptionItems ?? newOptions.map((name) => ({ name })));
  }, [initialOptions, initialAllowUsersToAdd, initialDefaultValue, initialOptionItems]);

  const addOption = () => {
    const newOptions = [...safeOptions, ""];
    const newItems = [...optionItems, { name: "" }];
    setOptions(newOptions);
    setOptionItems(newItems);
    setFocusNewInput(true);
    notifyParent(newOptions, allowUsersToAdd, defaultValue, newItems);
  };

  const removeOption = (index: number) => {
    const newOptions = safeOptions.filter(
      (_: string, i: number) => i !== index
    );
    const newItems = optionItems.filter((_, i) => i !== index);
    setOptions(newOptions);
    setOptionItems(newItems);

    let newDefaultValue = defaultValue;
    if (defaultValue === safeOptions[index]) {
      newDefaultValue = "";
      setDefaultValue("");
    }

    notifyParent(newOptions, allowUsersToAdd, newDefaultValue, newItems);
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...safeOptions];
    newOptions[index] = value;
    // Rename in place — preserve the row's id/isHidden/isSystem.
    const newItems = optionItems.map((item, i) =>
      i === index ? { ...item, name: value } : item
    );
    setOptions(newOptions);
    setOptionItems(newItems);

    let newDefaultValue = defaultValue;
    if (defaultValue === safeOptions[index] && value !== safeOptions[index]) {
      newDefaultValue = value;
      setDefaultValue(value);
    }

    notifyParent(newOptions, allowUsersToAdd, newDefaultValue, newItems);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const newOptions = [...safeOptions];
    const [draggedOption] = newOptions.splice(draggedIndex, 1);
    newOptions.splice(dropIndex, 0, draggedOption);

    // Move the item in lock-step with its option so id/flags follow the row.
    const newItems = [...optionItems];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(dropIndex, 0, draggedItem ?? { name: draggedOption });

    setOptions(newOptions);
    setOptionItems(newItems);
    setDraggedIndex(null);
    notifyParent(newOptions, allowUsersToAdd, defaultValue, newItems);
  };

  const handleSortAZ = () => {
    const paired = safeOptions.map((option, i) => ({
      option,
      item: optionItems[i] ?? { name: option }
    }));
    paired.sort((a, b) => a.option.localeCompare(b.option));
    const newOptions = paired.map((pair) => pair.option);
    const newItems = paired.map((pair) => pair.item);
    setOptions(newOptions);
    setOptionItems(newItems);
    notifyParent(newOptions, allowUsersToAdd, defaultValue, newItems);
  };

  const toggleAllowUsersToAdd = (checked: boolean) => {
    setAllowUsersToAdd(checked);
    notifyParent(safeOptions, checked, defaultValue, optionItems);
  };

  const handleDefaultValueChange = (value: string | number) => {
    const stringValue = String(value);
    setDefaultValue(stringValue);
    notifyParent(safeOptions, allowUsersToAdd, stringValue, optionItems);
  };

  const defaultValueOptions = [
    { id: NONE_OPTION_VALUE, displayName: t("noneOption") },
    ...safeOptions
      .filter((option: string) => option.trim() !== "")
      .map((option: string, index: number) => ({
        id: option,
        displayName:
          option ||
          t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.optionPlaceholder`, {
            index: index + 1
          })
      }))
  ];

  // Flag gates the extras (hide/show toggle, A–Z sort) only. System-row
  // read-only rendering is flag-independent — it keys off isSystem.
  const configurableExtrasEnabled =
    isEditableSystemField &&
    configurableSystemFieldsEnabled &&
    !configurableSystemFieldsLoading;

  const showHideToggle =
    configurableExtrasEnabled && typeof onHiddenToggle === "function";

  // For preset field editing, only show the disabled checkbox
  if (isPresetFieldEdit) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <SingleCheckbox
            id="allowUsersToAdd"
            label={t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.allowUsersToAdd`)}
            isChecked={true}
            onCheckedChange={() => {}}
            disabled={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <Text variant="bodySecondary">
            {t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.dropdownOptions`)}
          </Text>
          {configurableExtrasEnabled ? (
            <IconButton
              type="button"
              variant="iconNoBg"
              size="sm"
              iconColor="secondary"
              data-test="sort-options-az"
              onClick={handleSortAZ}
              aria-label={t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.sortAZ`, "Sort A–Z")}
              title={t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.sortAZ`, "Sort A–Z")}
            >
              <SortAZ />
            </IconButton>
          ) : null}
        </div>

        {!isEditableSystemField ? (
          <Text variant="bodyTertiary" className="text-sm">
            {t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.dropdownOptionsDescription`)}
          </Text>
        ) : null}

        <div className="flex flex-col gap-2">
          {safeOptions.map((option: string, index: number) => {
            const item = optionItems[index];
            const isHidden = item?.isHidden === true;
            return (
              <div
                key={
                  item?.id !== undefined ? `opt-${item.id}` : `opt-new-${index}`
                }
                className="flex gap-1 items-center"
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
              >
                <IconContainer
                  size="sm"
                  color="secondary"
                  className="cursor-grab"
                >
                  <GripVertical />
                </IconContainer>
                {isEditableSystemField && item?.isSystem === true ? (
                  <Text
                    variant="body"
                    className={`flex-1 py-1 px-2 ${isHidden ? "opacity-40" : ""}`}
                  >
                    {option}
                  </Text>
                ) : (
                  <Input
                    ref={
                      index === safeOptions.length - 1 && option === ""
                        ? newInputRef
                        : undefined
                    }
                    type="text"
                    placeholder={t(
                      `${OPTIONS_MANAGER_TRANSLATION_KEY}.optionPlaceholder`,
                      { index: index + 1 }
                    )}
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    containerClassName="flex-1"
                  />
                )}
                {isEditableSystemField && item?.isSystem === true ? (
                  showHideToggle ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <IconButton
                          type="button"
                          variant="iconNoBg"
                          size="sm"
                          iconColor="secondary"
                          onClick={async () => {
                            // Gate on the used-value confirmation; cancel = no-op.
                            if (onBeforeHide && !isHidden) {
                              const proceed = await onBeforeHide(item);
                              if (!proceed) return;
                            }
                            // Flip local state for feedback, then bubble up for the bulk Save.
                            setOptionItems((prev) =>
                              prev.map((it, i) =>
                                i === index ? { ...it, isHidden: !it.isHidden } : it
                              )
                            );
                            onHiddenToggle(item.id!);
                          }}
                          data-test={isHidden ? "unhide-option" : "hide-option"}
                          aria-label={
                            isHidden
                              ? t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.unhideOption`, "Unhide option")
                              : t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.hideOption`, "Hide option")
                          }
                        >
                          {isHidden ? <Unhide /> : <Hide />}
                        </IconButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isHidden
                          ? t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.show`, "Show")
                          : t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.hide`, "Hide")}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    // System values can't be deleted — disabled trash + tooltip
                    // (span wrapper lets the tooltip fire over the disabled button).
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="inline-flex">
                          <IconButton
                            type="button"
                            variant="iconNoBg"
                            size="sm"
                            iconColor="secondary"
                            disabled
                            data-test="delete-system-value-locked"
                            aria-label={t(
                              `${OPTIONS_MANAGER_TRANSLATION_KEY}.systemValueLocked`,
                              "System values can't be deleted"
                            )}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t(
                          `${OPTIONS_MANAGER_TRANSLATION_KEY}.systemValueLocked`,
                          "System values can't be deleted"
                        )}
                      </TooltipContent>
                    </Tooltip>
                  )
                ) : safeOptions.length > 1 ? (
                  <IconButton
                    type="button"
                    variant="iconNoBg"
                    onClick={() => removeOption(index)}
                    size="sm"
                    iconColor="secondary"
                  >
                    <Delete />
                  </IconButton>
                ) : null}
              </div>
            );
          })}
        </div>

        {safeOptions.length === 0 && (
          <div className="text-center py-4">
            <Text variant="bodyTertiary">
              {t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.noOptionsYet`)}
            </Text>
          </div>
        )}

        <div className="flex justify-start">
          <AddActionButton
            label={t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.addOption`)}
            onClick={addOption}
            textVariant="body"
            type="button"
          />
        </div>

        {!isEditableSystemField && (
          <SingleCheckbox
            id="allowUsersToAdd"
            label={t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.allowUsersToAdd`)}
            isChecked={allowUsersToAdd}
            onCheckedChange={toggleAllowUsersToAdd}
          />
        )}
      </div>
      {(!isEditableSystemField || showSystemFieldDefaultEntry) && (
        <div className="flex flex-col gap-3">
          <Text variant="bodySecondary">
            {t(`${OPTIONS_MANAGER_TRANSLATION_KEY}.defaultEntry`)}
          </Text>
          <SingleSelect
            fieldName="defaultValue"
            options={defaultValueOptions}
            selectedValue={defaultValue}
            onSelection={handleDefaultValueChange}
            placeholder={t(
              `${OPTIONS_MANAGER_TRANSLATION_KEY}.selectDefaultOption`
            )}
            variant="form"
          />
        </div>
      )}
    </div>
  );
};

export default OptionsManager;
